/**
 * PractitionerDiagnosticPage — OCM Complexity Diagnostic, full practitioner version
 * Route: /diagnostic/:engagementId  (consultant-facing, linked from dashboard)
 *
 * Differences from marketing version:
 *   - 12 dropdowns (8 context + 4 amplifiers), max 42 pts
 *   - Requires engagementId param → stored against the engagement in DB
 *   - Two-section layout: Core Context + Risk Amplifiers
 *   - Amplifier section flagged with visual callout
 */

import { useState, useMemo } from 'react'
import { useParams, Link } from 'react-router-dom'
import {
  TRIGGER_ITEMS,
  PRACTITIONER_DROPDOWNS,
  TIER_META,
  scorePractitioner,
  type TierKey,
} from '../data/diagnostic-items'
import styles from './DiagnosticPage.module.css'
import pStyles from './PractitionerDiagnosticPage.module.css'

const API_BASE = import.meta.env.VITE_API_BASE ?? 'https://clutch-ocm-scorer-uqrk7imnlq-uc.a.run.app'
const MAX_SCORE = 42 // practitioner: 8 triggers + 34 dropdown pts

// ── Types ─────────────────────────────────────────────────────────────────────

interface DiagnosticResult {
  diagnostic_run_id: number
  version: string
  engagement_id: number | null
  trigger_score: number
  total_score: number
  xocm_tier: TierKey
  tier_label: string
  tier_short: string
  interpretation: string
  recommendations: string[]
  top_drivers: { key: string; label: string; score: number }[]
  created_at: string
}

type PageState = 'form' | 'submitting' | 'result' | 'error'

const CONTEXT_DROPDOWNS = PRACTITIONER_DROPDOWNS.filter(d => d.section === 'context')
const AMPLIFIER_DROPDOWNS = PRACTITIONER_DROPDOWNS.filter(d => d.section === 'amplifiers')

// ── Component ─────────────────────────────────────────────────────────────────

export default function PractitionerDiagnosticPage() {
  const { engagementId } = useParams<{ engagementId: string }>()
  const parsedEngagementId = engagementId ? parseInt(engagementId, 10) : null

  const [triggers, setTriggers] = useState<Record<number, boolean>>({})
  const [inputs, setInputs] = useState<Record<string, number>>({})
  const [pageState, setPageState] = useState<PageState>('form')
  const [result, setResult] = useState<DiagnosticResult | null>(null)
  const [errorMsg, setErrorMsg] = useState('')

  // ── Live scoring
  const triggerCount = useMemo(() => Object.values(triggers).filter(Boolean).length, [triggers])
  const { total: liveTotal, tier: liveTier } = useMemo(
    () => scorePractitioner(triggerCount, inputs),
    [triggerCount, inputs]
  )
  const fillPct = Math.min(100, (liveTotal / MAX_SCORE) * 100)
  const allDropdownsFilled = PRACTITIONER_DROPDOWNS.every(d => inputs[d.key] !== undefined)

  function toggleTrigger(idx: number) {
    setTriggers(prev => ({ ...prev, [idx]: !prev[idx] }))
  }

  function setDropdown(key: string, value: number) {
    setInputs(prev => ({ ...prev, [key]: value }))
  }

  async function handleSubmit() {
    if (!allDropdownsFilled) return
    setPageState('submitting')
    setErrorMsg('')

    const triggerFlags: Record<string, boolean> = {}
    TRIGGER_ITEMS.forEach((_, idx) => {
      triggerFlags[String(idx)] = !!triggers[idx]
    })

    try {
      const res = await fetch(`${API_BASE}/diagnostic/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          version: 'practitioner',
          engagement_id: parsedEngagementId,
          trigger_flags: triggerFlags,
          inputs,
        }),
      })

      if (!res.ok) {
        const text = await res.text()
        throw new Error(text || `Server error ${res.status}`)
      }

      const data: DiagnosticResult = await res.json()
      setResult(data)
      setPageState('result')
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Unknown error'
      setErrorMsg(msg)
      setPageState('error')
    }
  }

  function handleReset() {
    setTriggers({})
    setInputs({})
    setResult(null)
    setPageState('form')
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  // ── Result view ───────────────────────────────────────────────────────────

  if (pageState === 'result' && result) {
    const tier = result.xocm_tier as TierKey
    const meta = TIER_META[tier]
    const maxDriverScore = Math.max(...result.top_drivers.map(d => d.score), 1)

    return (
      <div className={styles.page}>
        <div className={styles.hero} style={{ background: '#1a1a2e' }}>
          <p className={styles.heroEyebrow}>Practitioner Diagnostic · Engagement #{parsedEngagementId}</p>
          <h1 className={styles.heroTitle}>xOCM Complexity Result</h1>
          <p className={styles.heroSub}>
            Full 42-point diagnostic. Results saved to this engagement.
          </p>
        </div>

        <div className={styles.resultPage}>
          <div className={`${styles.resultHero} ${styles[meta.colorClass]}`}>
            <p className={styles.resultEyebrow}>xOCM Tier</p>
            <h2 className={styles.resultTierLabel}>{result.tier_label}</h2>
            <p className={styles.resultTierShort}>{result.tier_short}</p>
            <p className={styles.resultScoreLine}>
              Total: {result.total_score} / {MAX_SCORE} &nbsp;·&nbsp;
              Triggers: {result.trigger_score} &nbsp;·&nbsp;
              Inputs: {result.total_score - result.trigger_score}
            </p>
          </div>

          <div className={styles.resultCard}>
            <p className={styles.resultSectionTitle}>What this means</p>
            <p className={styles.resultInterpretation}>{result.interpretation}</p>
          </div>

          <div className={styles.resultCard}>
            <p className={styles.resultSectionTitle}>Recommended next steps</p>
            <ul className={styles.recommendationList}>
              {result.recommendations.map((rec, i) => (
                <li key={i}>{rec}</li>
              ))}
            </ul>
          </div>

          {result.top_drivers.length > 0 && (
            <div className={styles.resultCard}>
              <p className={styles.resultSectionTitle}>Top complexity drivers</p>
              <div className={styles.driversGrid}>
                {result.top_drivers.map(d => (
                  <div className={styles.driverRow} key={d.key}>
                    <span className={styles.driverLabel}>{d.label}</span>
                    <div className={styles.driverTrack}>
                      <div
                        className={styles.driverFill}
                        style={{ width: `${(d.score / maxDriverScore) * 100}%` }}
                      />
                    </div>
                    <span className={styles.driverScore}>{d.score}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className={styles.resetWrap}>
            <button className={styles.resetBtn} onClick={handleReset}>
              Run another diagnostic
            </button>
            {parsedEngagementId && (
              <Link to="/dashboard" className={styles.resetBtn} style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
                ← Back to dashboard
              </Link>
            )}
          </div>
        </div>
      </div>
    )
  }

  // ── Form view ─────────────────────────────────────────────────────────────

  return (
    <div className={styles.page}>
      <div className={styles.hero} style={{ background: '#1a1a2e' }}>
        <p className={styles.heroEyebrow}>Practitioner Diagnostic · Full Version</p>
        <h1 className={styles.heroTitle}>xOCM Complexity Assessment</h1>
        <p className={styles.heroSub}>
          42-point diagnostic including risk amplifiers. Results are saved against the engagement
          and visible in the dashboard.
          {parsedEngagementId && ` Engagement: #${parsedEngagementId}`}
        </p>
      </div>

      <div className={styles.formWrap}>
        {/* Triggers */}
        <div className={styles.card}>
          <p className={styles.cardTitle}>Step 1 — Change triggers (8 pts max)</p>
          <p className={styles.cardDesc}>
            Check every statement that applies to this initiative.
          </p>
          <div className={styles.triggerGrid}>
            {TRIGGER_ITEMS.map((text, idx) => (
              <label
                key={idx}
                className={`${styles.triggerRow} ${triggers[idx] ? styles.checked : ''}`}
              >
                <input
                  type="checkbox"
                  className={styles.triggerCheckbox}
                  checked={!!triggers[idx]}
                  onChange={() => toggleTrigger(idx)}
                />
                <span className={styles.triggerLabel}>{text}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Core context dropdowns */}
        <div className={styles.card}>
          <p className={styles.cardTitle}>Step 2 — Core context (max 20 pts)</p>
          <p className={styles.cardDesc}>
            Select the most accurate description for each factor.
          </p>
          <div className={styles.dropdownList}>
            {CONTEXT_DROPDOWNS.map(item => (
              <div key={item.key} className={styles.dropdownItem}>
                <p className={styles.dropdownLabel}>{item.label}</p>
                {item.hint && <p className={styles.dropdownHint}>{item.hint}</p>}
                <select
                  className={`${styles.dropdownSelect} ${inputs[item.key] !== undefined ? styles.filled : ''}`}
                  value={inputs[item.key] !== undefined ? String(inputs[item.key]) : ''}
                  onChange={e => {
                    const v = e.target.value
                    if (v !== '') setDropdown(item.key, Number(v))
                  }}
                >
                  <option value="">Select one…</option>
                  {item.options.map(opt => (
                    <option key={opt.value} value={String(opt.value)}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        </div>

        {/* Risk amplifiers */}
        <div className={`${styles.card} ${pStyles.amplifiersCard}`}>
          <div className={pStyles.amplifiersBadge}>Risk Amplifiers</div>
          <p className={styles.cardTitle}>Step 3 — Risk amplifiers (max 14 pts)</p>
          <p className={styles.cardDesc}>
            These factors don't directly change what is happening, but they compound the
            difficulty of achieving adoption. Score them based on current conditions, not on
            what you plan to do about them.
          </p>
          <div className={styles.dropdownList}>
            {AMPLIFIER_DROPDOWNS.map(item => (
              <div key={item.key} className={styles.dropdownItem}>
                <p className={styles.dropdownLabel}>{item.label}</p>
                {item.hint && <p className={styles.dropdownHint}>{item.hint}</p>}
                <select
                  className={`${styles.dropdownSelect} ${inputs[item.key] !== undefined ? styles.filled : ''}`}
                  value={inputs[item.key] !== undefined ? String(inputs[item.key]) : ''}
                  onChange={e => {
                    const v = e.target.value
                    if (v !== '') setDropdown(item.key, Number(v))
                  }}
                >
                  <option value="">Select one…</option>
                  {item.options.map(opt => (
                    <option key={opt.value} value={String(opt.value)}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        </div>

        {/* Live score bar */}
        <div className={styles.scoreBarCard}>
          <span className={styles.scoreBarLabel}>Complexity score</span>
          <div className={styles.scoreBarTrack}>
            <div
              className={`${styles.scoreBarFill} ${styles[`tier${liveTier}`]}`}
              style={{ width: `${fillPct}%` }}
            />
          </div>
          <span className={styles.scoreNum}>{liveTotal}</span>
          <span className={`${styles.scoreTierBadge} ${styles[`tier${liveTier}`]}`}>
            {liveTier}
          </span>
        </div>

        {pageState === 'error' && (
          <div className={styles.errorBanner}>
            Could not save results: {errorMsg}
          </div>
        )}

        <div className={styles.submitWrap}>
          <button
            className={styles.submitBtn}
            onClick={handleSubmit}
            disabled={!allDropdownsFilled || pageState === 'submitting'}
          >
            {pageState === 'submitting' ? 'Scoring…' : 'Generate xOCM recommendation →'}
          </button>
        </div>
      </div>
    </div>
  )
}
